import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  AfterContentInit,
  ViewEncapsulation,
  afterRenderEffect,
  computed,
  forwardRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { InputNumberDirective } from '../input-number.directive';

@Component({
    selector: 'app-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputComponent),
            multi: true
        }
    ],
    imports: [FormsModule, InputNumberDirective]
})
export class InputComponent implements ControlValueAccessor, AfterContentInit, AfterViewInit {
  private cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  placeholder = input('');
  disabled = input(false);
  readonly = input(false);
  min = input(-Infinity);
  max = input(Infinity);
  step = input<number | 'any'>(1);
  icon = input<'search' | 'email' | 'lock' | 'user' | false>(false);
  value = input<string>('');
  type = input<'text' | 'textarea' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'date'>('text');
  size = input<'sm' | 'md' | 'lg' | 'xs'>('md');
  button = input<{ label: string; variant?: string; size?: string; disabled?: boolean } | undefined>(undefined);
  fakeButton = input(false);
  autocomplete = input<string>('off');
  /** Accessible name when no visible label can point at the field (it sits in a shadow root). */
  ariaLabel = input<string | null>(null);
  /** Most characters the field accepts. */
  maxlength = input<number | null>(null);
  /**
   * Ids of elements that describe the field (a hint, a character counter), in
   * the tree that holds this component. The field sits in a shadow root, so the
   * ids are resolved here and linked as elements.
   */
  ariaDescribedBy = input<string | null>(null);
  /** Visible text rows of a textarea; it grows from there when `autoResize` is on. */
  rows = input(3);
  /** A textarea grows with its text between `minHeight` and `maxHeight` (px). */
  autoResize = input<{ enabled: boolean; maxHeight?: number; minHeight?: number }>({ enabled: false });
  /**
   * Marks the field invalid. Left null, a bound form control decides: invalid
   * once it has been touched.
   */
  invalid = input<boolean | null>(null);
  gapAndButtonMargin = input(false);
  buttonClick = output<void>();

  private readonly textarea = viewChild<ElementRef<HTMLTextAreaElement>>('textareaElement');
  private readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor() {
    afterRenderEffect(() => {
      const field = (this.textarea() ?? this.inputElement())?.nativeElement;
      if (field) linkDescriptions(field, this.host.nativeElement, this.ariaDescribedBy());
    });
  }

  /** Internal disabled state that can be toggled by CVA setDisabledState */
  _disabledByCva = signal(false);

  /** Effective disabled state: true if either the input signal or the CVA says disabled */
  isDisabled = computed(() => this.disabled() || this._disabledByCva());

  /** Invalid and touched state of the bound form control, refreshed on its events. */
  private readonly controlInvalid = signal(false);

  /** Whether the field shows and announces its invalid state. */
  readonly isInvalid = computed(() => !this.isDisabled() && (this.invalid() ?? this.controlInvalid()));

  /**
   * Writable signal linked to the `value` input.
   * - Follows `value()` when the parent changes the binding
   * - Can be independently set by `writeValue()` (CVA) or user input
   * - Unlike effect+signal, linkedSignal does NOT overwrite CVA-set values
   */
  private _value = linkedSignal<string, string>({
    source: this.value,
    computation: (src) => src || ''
  });
  private onChange = (value: string) => {};
  private onTouched = () => {};

  // Password visibility toggle
  showPassword = false;

  // After the node's own directives ran: formControlName links its control in ngOnChanges.
  ngAfterContentInit(): void {
    const control = this.injector.get(NgControl, null, { self: true, optional: true })?.control;
    if (!control) return;
    const sync = (): void => this.controlInvalid.set(control.invalid && control.touched);
    sync();
    const sub = control.events.subscribe(() => {
      sync();
      this.cdr.markForCheck();
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  ngAfterViewInit(): void {
    if (this.type() === 'textarea' && this.autoResize().enabled) this.resizeTextarea();
  }

  get internalValue(): string {
    return this._value();
  }

  set internalValue(val: string) {
    if (val !== this._value()) {
      this._value.set(val);
      this.onChange(val);
      this.cdr.detectChanges();
    }
  }

  writeValue(value: string): void {
    this._value.set(value || '');
    this.cdr.detectChanges();
    if (this.type() === 'textarea' && this.autoResize().enabled) this.resizeTextarea();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
    this.cdr.detectChanges();
  }

  onInput(e: Event): void {
    const target = e.target as HTMLInputElement;
    this.internalValue = target.value;
    if (this.type() === 'textarea' && this.autoResize().enabled) this.resizeTextarea();
  }

  onBlur(): void {
    this.onTouched();
  }

  /** The number directive tidies or clamps the text on blur; the form value follows it. */
  onNumberBlur(e: Event): void {
    this.internalValue = (e.target as HTMLInputElement).value;
    this.onTouched();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  get actualInputType(): string {
    if (this.type() === 'password') {
      return this.showPassword ? 'text' : 'password';
    }
    return this.type();
  }

  /** Native min/max/step only mean something on number and date fields, and only when set. */
  get minAttr(): number | null {
    return this.usesRange && Number.isFinite(this.min()) ? this.min() : null;
  }

  get maxAttr(): number | null {
    return this.usesRange && Number.isFinite(this.max()) ? this.max() : null;
  }

  get stepAttr(): number | 'any' | null {
    return this.type() === 'number' ? this.step() : null;
  }

  private get usesRange(): boolean {
    return this.type() === 'number' || this.type() === 'date';
  }

  /** Grows the textarea to fit its text within the configured bounds. */
  private resizeTextarea(): void {
    const el = this.textarea()?.nativeElement;
    if (!el) return;
    const { minHeight, maxHeight } = this.autoResize();
    el.style.height = 'auto';
    const min = minHeight ?? 0;
    const max = maxHeight ?? Number.POSITIVE_INFINITY;
    const next = Math.max(min, Math.min(el.scrollHeight + this.borderHeight(el), max));
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight + this.borderHeight(el) > max ? 'auto' : 'hidden';
  }

  private borderHeight(el: HTMLElement): number {
    const style = getComputedStyle(el);
    return (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
  }
}

/**
 * Points the field's description at the elements with the given ids, found in
 * the tree around the host (the field itself is in the host's shadow root,
 * where those ids don't resolve). Falls back to the plain attribute where
 * element references aren't supported.
 */
function linkDescriptions(field: HTMLElement, host: HTMLElement, ids: string | null): void {
  const list = (ids ?? '').split(/\s+/).filter(Boolean);
  if (!('ariaDescribedByElements' in HTMLElement.prototype)) {
    if (list.length) field.setAttribute('aria-describedby', list.join(' '));
    else field.removeAttribute('aria-describedby');
    return;
  }
  const root = host.getRootNode() as Document | ShadowRoot;
  const found = list.map((id) => root.getElementById?.(id) ?? host.ownerDocument.getElementById(id)).filter((el): el is HTMLElement => !!el);
  field.ariaDescribedByElements = found.length ? found : null;
}
